<template>
  <layout>
    <template #header-left> Batch Images </template>
    <template #header-right>
      <a
        v-if="sortedBatchMediaData.length"
        :href="getBatchMediaDownloadLink()"
        download
        class="btn"
      >
        Download All Images
      </a>
    </template>
    <template #sidebar>
      <sidebar-list>
        <sidebar-list-item
          v-for="batchItem of sortedBatchMediaData"
          :key="batchItem.claimId"
          :active="currentClaimId === batchItem.claimId"
          :ref="el => setSidebarClaimRef(el, batchItem.claimId)"
          @click="onSelectClaim(batchItem.claimId)"
        >
          Claim # {{ batchItem.claimId }}
        </sidebar-list-item>
      </sidebar-list>
    </template>
    <template #main v-if="batchMediaError">
      <div>
        {{ batchMediaError.message }}
      </div>
    </template>
    <template #main v-else>
      <div v-if="loadingBatchMedia">
        Loading media data&hellip;
      </div>
      <div v-else class="gallery-list">
        <img
          v-for="media of currentClaimMedia"
          :key="media.id"
          :src="media.thumbnail"
          :alt="`${currentClaimId}_${media.id}`"
          class="gallery-list-item"
          @click="onSelectMedia(media)"
        />
      </div>
    </template>
  </layout>
  <modal v-if="showModal" @close="toggleModal" class="modal-claim-media">
    <video v-if="currentMedia.isVideo" :src="currentMedia.link" controls></video>
    <img v-else :src="currentMedia.link" :alt="currentMedia.id" />
  </modal>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, onMounted, reactive, ref, watch } from 'vue';
import Layout from '@/components/Layout.vue';
import { useRoute } from 'vue-router';
import SidebarList from '@/components/SidebarList.vue';
import SidebarListItem from '@/components/SidebarListItem.vue';
import Modal from '@/components/Modal.vue';

import { IClaimBatchItem, IClaimMedia, QueryRecord } from '@/types';
import { useBatch } from '@/hooks';

export default defineComponent({
  name: 'Gallery',
  components: {
    Layout,
    SidebarList,
    SidebarListItem,
    Modal,
  },
  setup() {
    const route = useRoute();
    const batchId = route.params.id.toString();
    const claimIdProp = Number(route.query.claim) || null;
    const {
      loadingBatchMedia,
      batchMediaData: _unsortedBatchMediaData,
      batchMediaError,
      getBatchMedia,
      getBatchMediaDownloadLink,
    }: {
      loadingBatchMedia: ComputedRef<boolean>;
      batchMediaData: ComputedRef<IClaimBatchItem[] | undefined>;
      batchMediaError: ComputedRef<string | null>;
      getBatchMedia: (query?: QueryRecord | undefined) => Promise<void>;
      getBatchMediaDownloadLink: () => string;
    } = useBatch(batchId);

    const showModal = ref<boolean>(false);
    const currentClaimId = ref<number | null>(claimIdProp);

    const sortedBatchMediaData = computed<IClaimBatchItem[] | undefined>(() => {
      const temp: IClaimBatchItem[] = _unsortedBatchMediaData.value || [];
      return temp.sort((a, b) => a.claimId - b.claimId);
    });

    const _currentBatchMediaItem = computed<IClaimBatchItem | undefined>(() => {
      const temp: IClaimBatchItem[] = _unsortedBatchMediaData.value || [];
      return temp.find((batchItem: IClaimBatchItem) => batchItem.claimId === currentClaimId.value);
    });

    const currentClaimMedia = computed<IClaimMedia[]>(() => {
      return _currentBatchMediaItem.value?.media || [];
    });

    const currentMedia = ref<IClaimMedia | null>(null);

    const toggleModal = () => {
      showModal.value = !showModal.value;
    };

    const onSelectClaim = (claimId: number) => {
      currentClaimId.value = claimId;
    };

    const onSelectMedia = (media: IClaimMedia) => {
      toggleModal();
      currentMedia.value = media;
    };

    const _sidebarClaimRefs: { [key: string]: HTMLElement } = reactive({});

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setSidebarClaimRef = (elementRef: any, claimId: number) => {
      if (elementRef && claimId) {
        _sidebarClaimRefs[claimId] = elementRef.$el;
      }
    };

    // default currentClaimId to the first item in the list unless we have something valid selected already
    watch(sortedBatchMediaData, () => {
      if (sortedBatchMediaData.value?.length) {
        if (
          !currentClaimId.value ||
          !sortedBatchMediaData.value.some(
            (item: IClaimBatchItem) => item.claimId === currentClaimId.value
          )
        ) {
          currentClaimId.value = sortedBatchMediaData.value[0].claimId;
        }
      }
    });

    if (claimIdProp) {
      const stopper = watch(
        () => _sidebarClaimRefs[claimIdProp],
        (element: HTMLElement) => {
          element.scrollIntoView();
          stopper();
        }
      );
    }

    onMounted(() => {
      getBatchMedia();
    });

    return {
      batchId,
      setSidebarClaimRef,
      showModal,
      toggleModal,
      onSelectClaim,
      onSelectMedia,
      loadingBatchMedia,
      sortedBatchMediaData,
      batchMediaError,
      currentClaimId,
      currentClaimMedia,
      currentMedia,
      getBatchMediaDownloadLink,
    };
  },
});
</script>

<style lang="scss">
.gallery-list {
  background-color: var(--white);
  width: 100%;
  height: calc(100% - var(--space-4) * 2);
  overflow: auto;
  padding-top: var(--space-4);

  .gallery-list-item {
    max-width: 10rem;
    display: inline-block;
    margin-left: var(--space-4);
    margin-bottom: var(--space-4);
    cursor: pointer;
  }
}
</style>
